import posthog from 'posthog-js';
import type { AnalyticsProvider, AnalyticsUser } from '@systemzero-inc/ui/analytics';

/**
 * PostHog implementation of the analytics provider interface.
 *
 * Wraps the PostHog JS SDK to match the provider-agnostic interface defined
 * in the UI library. Allows the app to swap analytics providers without
 * changing tracking call sites.
 */
export class PostHogProvider implements AnalyticsProvider {
  private superProps: Record<string, string> = {};

  /**
   * Initialize PostHog with the given API key and configuration options.
   *
   * Must be called before using any other methods. Safe to call multiple times
   * (subsequent calls are no-ops).
   *
   * @param apiKey - PostHog project API key
   * @param options - Configuration options (apiHost, uiHost, environment, service).
   *   - uiHost defaults to https://us.posthog.com if not provided
   */
  init(apiKey: string, options?: Record<string, unknown>): void {
    const apiHost = typeof options?.apiHost === 'string' ? options.apiHost : undefined;
    const uiHost = typeof options?.uiHost === 'string' && options.uiHost ? options.uiHost : 'https://us.posthog.com';
    const environment = typeof options?.environment === 'string' ? options.environment : undefined;
    const service = typeof options?.service === 'string' ? options.service : undefined;

    if (environment) this.superProps['environment'] = environment;
    if (service) this.superProps['service'] = service;

    if (!posthog.__loaded) {
      posthog.init(apiKey, {
        // In production this is a same-origin proxy path (e.g. /relay-9x2m4q); in dev it's a full posthog.com URL
        api_host: apiHost,
        // Overrides the toolbar/dashboard link target so clicking "View in PostHog" from the toolbar resolves to
        // the actual PostHog UI even when api_host is a reverse-proxy path. Defaults to US cloud.
        ui_host: uiHost,
        person_profiles: 'identified_only',
        capture_pageview: false, // Manual pageview tracking only
        capture_pageleave: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
        },
      });
    }

    if (Object.keys(this.superProps).length > 0) {
      posthog.register(this.superProps);
    }
  }

  /**
   * Identify the current user with PostHog.
   *
   * Links all subsequent events to this user. The Auth0 `sub` claim is used
   * as the `distinct_id` to ensure consistent cross-session tracking.
   *
   * @param user - User information (id from Auth0 sub, email, name, orgId)
   */
  identify(user: AnalyticsUser): void {
    posthog.identify(user.id, {
      organization_id: user.organizationId,
    });

    if (user.organizationId) {
      posthog.group('organization', user.organizationId);
    }
  }

  /**
   * Track an analytics event.
   *
   * Events are sent to PostHog with the given name and optional properties.
   * Use event constants from `@systemzero-inc/ui/analytics` to ensure
   * consistency across the platform.
   *
   * @param event - Event name (e.g., 'inbox_item_viewed')
   * @param properties - Optional event metadata
   */
  track(event: string, properties?: Record<string, unknown>): void {
    posthog.capture(event, properties);
  }

  /**
   * Track a page view.
   *
   * @param name - Page name or path
   * @param properties - Optional page metadata
   */
  page(name?: string, properties?: Record<string, unknown>): void {
    posthog.capture('$pageview', { ...properties, page: name });
  }

  /**
   * Reset the current user's session.
   *
   * Called on logout to ensure the next user identified is treated as a new
   * session. Clears all stored user properties and session data.
   */
  reset(): void {
    posthog.reset();
    // Re-register super properties after reset — posthog.reset() clears persisted
    // super properties, so we must re-apply them immediately.
    if (Object.keys(this.superProps).length > 0) {
      posthog.register(this.superProps);
    }
  }
}
